import { createClient } from '@supabase/supabase-js';
import type { Session } from '@supabase/supabase-js';

const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL as string) ?? '';
const supabaseAnonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY as string) ?? '';

// Demo mode when credentials are missing or placeholder
export const isDemoMode =
  !supabaseUrl ||
  !supabaseAnonKey ||
  supabaseUrl.includes('placeholder') ||
  supabaseUrl.includes('your-project');

// Dummy JWT-shaped key so Supabase client doesn't reject it in demo mode
const DEMO_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJyb2xlIjoiYW5vbiIsImlhdCI6MTYwMDAwMDAwMCwiZXhwIjoxOTk5OTk5OTk5fQ.placeholder';

export const supabase = createClient(
  isDemoMode ? 'https://placeholder.supabase.co' : supabaseUrl,
  isDemoMode ? DEMO_KEY : supabaseAnonKey,
  isDemoMode
    ? {}
    : {
        auth: {
          autoRefreshToken: true,
          persistSession: true,
          detectSessionInUrl: true,
        },
      }
);

// Cache the in-flight session request so concurrent callers (e.g. React Strict Mode
// double-invoke) share a single Web Lock acquisition instead of racing.
let _sessionPromise: ReturnType<typeof supabase.auth.getSession> | null = null;

// Helper function to get current session
export async function getSession() {
  if (isDemoMode) {
    return { data: { session: null }, error: null };
  }
  if (!_sessionPromise) {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const timeout = new Promise<{ data: { session: null }; error: null }>((resolve) => {
      timeoutId = setTimeout(
        () => resolve({ data: { session: null }, error: null }),
        4000
      );
    });

    _sessionPromise = Promise.race([
      supabase.auth.getSession(),
      timeout,
    ]).finally(() => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      // Clear after resolution so future calls (e.g. after sign-out) get a fresh result
      _sessionPromise = null;
    });
  }
  return _sessionPromise;
}

// Helper function to sign out
export async function signOut() {
  if (isDemoMode) {
    return { error: null };
  }
  return await supabase.auth.signOut();
}

function makeDemoSession(userId: string, email: string): Session {
  const demoUser = {
    id: userId,
    email,
    app_metadata: {},
    user_metadata: {},
    aud: 'authenticated',
    created_at: new Date().toISOString(),
  } as Session['user'];

  return {
    access_token: 'demo-token',
    refresh_token: 'demo-refresh',
    expires_in: 3600,
    token_type: 'bearer',
    user: demoUser,
  };
}

// Helper function for sign up
export async function signUp(email: string, password: string, metadata?: Record<string, unknown>) {
  if (isDemoMode) {
    const userId = 'demo-user-' + Date.now();
    const session = makeDemoSession(userId, email);
    return {
      data: {
        user: { ...session.user, user_metadata: metadata ?? {} },
        session,
      },
      error: null,
    };
  }
  return await supabase.auth.signUp({
    email,
    password,
    options: { data: metadata },
  });
}

// Helper function for sign in
export async function signIn(email: string, password: string) {
  if (isDemoMode) {
    const session = makeDemoSession('demo-user', email);
    return {
      data: { user: session.user, session },
      error: null,
    };
  }
  return await supabase.auth.signInWithPassword({
    email,
    password,
  });
}
